import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { PayrollPeriodsResponse } from "@/actions/ssr/payroll/getPayrollPeriodsSSR";
import type {
  PayrollKpi,
  PayrollRecord,
} from "@/components/payroll/payrollTypes";
import type { PayrollSummaryResponse } from "@/actions/ssr/payroll/getPayrollSummarySSR";

interface PayrollState {
  results: PayrollRecord[];
  count: number;
  total_pages: number;
  current_page: number;
  kpi: PayrollKpi;
  payrollSummary: PayrollSummaryResponse;
  payrollDateFilter: { from: string | null; to: string | null };
  periodsClientFetched: boolean;
  summaryClientFetched: boolean;
}

const initialState: PayrollState = {
  results: [],
  count: 0,
  total_pages: 0,
  current_page: 1,
  kpi: {
    totalPayroll: 0,
    weekPayrollAmount: 0,
    weekPayrollLabel: "",
    pendingAmount: 0,
    pendingStaffDueThisWeek: 0,
    taxDeductionAmount: 0,
    taxDeductionTrendPct: null,
    paidCount: 0,
    pendingCount: 0,
    overdueCount: 0,
  },
  payrollSummary: {
    payable: { amount: 0, currency: "GBP" },
    pending: { amount: 0, currency: "GBP" },
    paid: { amount: 0, currency: "GBP" },
  },
  payrollDateFilter: { from: null, to: null },
  periodsClientFetched: false,
  summaryClientFetched: false,
};

const payrollSlice = createSlice({
  name: "payroll",
  initialState,
  reducers: {
    setPayrollPeriods(state, action: PayloadAction<PayrollPeriodsResponse>) {
      state.periodsClientFetched = true;
      state.results = action.payload.results;
      state.count = action.payload.count;
      state.total_pages = action.payload.total_pages;
      state.current_page = action.payload.current_page;
      state.kpi = action.payload.kpi;
    },
    setPayrollPeriodsFromSSR(
      state,
      action: PayloadAction<PayrollPeriodsResponse>,
    ) {
      if (state.periodsClientFetched) return;
      state.results = action.payload.results;
      state.count = action.payload.count;
      state.total_pages = action.payload.total_pages;
      state.current_page = action.payload.current_page;
      state.kpi = action.payload.kpi;
    },
    setPayrollSummary(state, action: PayloadAction<PayrollSummaryResponse>) {
      state.summaryClientFetched = true;
      state.payrollSummary = action.payload;
    },
    setPayrollSummaryFromSSR(
      state,
      action: PayloadAction<PayrollSummaryResponse>,
    ) {
      if (state.summaryClientFetched) return;
      state.payrollSummary = action.payload;
    },
    setPayrollDateFilter(
      state,
      action: PayloadAction<{ from: string | null; to: string | null }>,
    ) {
      state.payrollDateFilter = action.payload;
    },
  },
});

export const {
  setPayrollPeriods,
  setPayrollPeriodsFromSSR,
  setPayrollSummary,
  setPayrollSummaryFromSSR,
  setPayrollDateFilter,
} = payrollSlice.actions;
export default payrollSlice.reducer;
